import { Directive, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { FormGroup, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { map, switchMap, take } from 'rxjs/operators';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { keyBy } from 'lodash';

import { VariationValue } from '@harnessio/ff-javascript-client-sdk/dist/types';
import { DsModalService, DsModalVariant } from '@levelaccess/design-system';
import {
  IDclConfig,
  ITableColumn,
  ITableConfig,
  ITableEmptyState,
  ITableRow,
  SortEvent,
} from '../../../components/table/ngb-table/utilities/ngb-table.interface';
import { IPaginationParams } from '../../../interfaces/pagination.interface';
import { ITenant, ITenantsResponse, ITenantWithPackageData } from '../../../../../shared/interfaces/tenant.interface';
import { $sortingOrder } from '../../../../../shared/constants/sort';
import { $paginationParams } from '../../../constants/pagination-params';
import { AclSecurityAdapter } from '../../../services/acl.service';
import { FunctionalArea, SecureAction } from '../../../../../shared/constants/security';
import { AppConfigService } from '../../../services/app-config.service';
import { TranslateService } from '../../../translate/translate.service';
import { TenantService } from '../../../services/tenant.service';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { UserAclService } from '../../../services/user-acl.service';
import { NotificationService } from '../../../services/notification.service';
import { NotificationPosition } from '../../../models/notification.model';
import { LoadErrorHandler } from '../../../services/load-error-handler';
import { PaginationService } from '../../../services/pagination.service';
import { RoutingService } from '../../../services/routing.service';
import { AngularUtility } from '../../../utility/angular.utility';
import { $tenant, $tenants, $tenantMetadata, TenantStatus } from '../../../../../shared/constants/tenant';
import { NgbTableUtilities } from '../../../components/table/ngb-table/utilities/ngb-table.utilities';
import { IDropdownItem } from '../../../components/table/ngb-table/cells/dropdown-cell/dropdown-cell.component';
import { SharedCommonUtility } from '../../../../../shared/utils/common.utility';
import { Api } from '../../../../../shared/constants/api';
import { ILinkCellConfig } from '../../../components/table/ngb-table/cells/link-cell/link-cell.component';
import { ITextCellConfig } from '../../../components/table/ngb-table/cells/text-cell/text-cell.component';
import { $academy } from '../../../../../shared/constants/academy';
import { IPackageAddonName, IPackageInstanceIdName } from '../../../../../shared/interfaces/packaging.interface';
import { PackageService } from '../../../services/package.service';
import { $package, $packageAddon, $packageInstance } from '../../../../../shared/constants/packaging';
import { FeatureFlagService } from '../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../shared/interfaces/feature-flag.interface';
import { UserService } from '../../../services/user.service';
import { PromptConfirmationModalComponent } from '../../../components/modals/prompt-confirmation-modal/prompt-confirmation-modal.component';
import { PromptConfirmationModalOptions } from '../../../interfaces/modal-dialog.interface';

enum TableColumn {
  NAME = 'name',
  PACKAGE = 'package',
  ADDONS = 'addons',
  SSO_CONFIG = 'ssoConfig',
  ACADEMY_CONFIG = 'academyConfig',
}

export enum FormField {
  SEARCH = 'search',
}

export interface IOrganizationsPaginationParams extends IPaginationParams {
  [FormField.SEARCH]: string;
}

@Directive()
export abstract class AbstractOrganizationsComponent implements OnInit, OnDestroy {
  public static readonly PAGINATION_PAGE_SIZE: number = 10;
  public readonly FormField: typeof FormField = FormField;

  protected readonly sortableColumns: string[] = [TableColumn.NAME];
  protected readonly formFields: string[] = [FormField.SEARCH];
  protected readonly defaultPaginationParams: IOrganizationsPaginationParams = {
    [$paginationParams.pageNumber]: 1,
    [$paginationParams.pageSize]: undefined,
    [$paginationParams.sortBy]: undefined,
    [$paginationParams.direction]: undefined,
    [FormField.SEARCH]: undefined,
  };

  protected subscription: Subscription = new Subscription();
  protected reloadTenants$: BehaviorSubject<void> = new BehaviorSubject(null);
  protected paginationParams$: BehaviorSubject<IOrganizationsPaginationParams> = new BehaviorSubject(
    this.defaultPaginationParams,
  );

  public form: UntypedFormGroup;
  public tableConfig: ITableConfig;
  public tenantsData$: Observable<ITenantsResponse>;
  public totalRows$: Observable<number>;
  public tableRows$: Observable<ITableRow[]>;
  public canCreateTenant: boolean = false;
  public canUpdateTenant: boolean = false;
  public canDeleteTenant: boolean = false;
  public tenantBaseUrl: string = '';
  public pageTitle: string = '';
  public pageNumber: number;
  public sortingOrder: Record<string, $sortingOrder>;

  public canReadWorkspace: boolean = false;
  public canReadDigitalAssets: boolean = false;

  private isSelfManagementEnabled$: Observable<boolean>;
  private tenantDecommissionFF$: Observable<boolean>;

  abstract getTenants$(params: IOrganizationsPaginationParams): Observable<ITenantsResponse>;
  abstract getTableEmptyState(): ITableEmptyState;

  protected constructor(
    protected appConfigService: AppConfigService,
    protected translateService: TranslateService,
    protected tenantService: TenantService,
    protected formBuilder: UntypedFormBuilder,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected errorHandlerService: ErrorHandlerService,
    protected userAclService: UserAclService,
    protected notificationService: NotificationService,
    protected loadErrorHandler: LoadErrorHandler,
    protected paginationService: PaginationService,
    protected routingService: RoutingService,
    protected packageService: PackageService,
    protected featureFlagService: FeatureFlagService,
    protected userService: UserService,
    protected dsModalService: DsModalService,
  ) {
    this.pageTitle = this.routingService.getPageTitle();
    this.form = this.buildForm();
    this.tenantBaseUrl = this.appConfigService.getTenantBaseUrl();

    this.subscribeToPermission(FunctionalArea.tenants, SecureAction.create, 'canCreateTenant', true);
    this.subscribeToPermission(FunctionalArea.tenants, SecureAction.update, 'canUpdateTenant', true);
    this.subscribeToPermission(FunctionalArea.tenants, SecureAction.delete, 'canDeleteTenant', true);
    this.subscribeToPermission(FunctionalArea.digital_properties, SecureAction.read, 'canReadDigitalAssets', false);
    this.subscribeToPermission(FunctionalArea.workspaces, SecureAction.read, 'canReadWorkspace', false, () =>
      this.reloadTenants$.next(),
    );
    this.isSelfManagementEnabled$ = this.featureFlagService
      .variation$(FeatureFlagCollection.selfManageOrgHierarchy, false)
      .pipe(map((value: VariationValue) => value === true));
    this.tenantDecommissionFF$ = this.featureFlagService
      .variation$(FeatureFlagCollection.tenantDecommission, false)
      .pipe(map((value: VariationValue) => value === true));
  }

  private buildForm(): FormGroup {
    return this.formBuilder.group({
      [FormField.SEARCH]: this.formBuilder.control(''),
    });
  }

  private subscribeToPermission(
    functionalArea: FunctionalArea,
    action: SecureAction,
    targetProperty: keyof this,
    useEntityLevel: boolean = false,
    postSubscribe?: () => void,
  ): void {
    this.subscription.add(
      this.userAclService
        .createCheckAccessForCurrentUser()
        .pipe(
          map((adapter: AclSecurityAdapter): boolean => {
            let permissionAdapter = adapter;
            if (useEntityLevel) {
              permissionAdapter = adapter.useEntityLevel();
            }
            return permissionAdapter.useFunctionalActions({ [functionalArea]: [action] }).check();
          }),
        )
        .subscribe((hasPermission: boolean) => {
          (this[targetProperty] as any) = hasPermission;
          postSubscribe?.();
        }),
    );
  }

  public ngOnInit(): void {
    this.tenantsData$ = this.getTenantData$();
    this.totalRows$ = this.getTotalRows$();
    this.tableRows$ = this.getTableRows$();

    this.subscribeToPaginationParams();
    this.subscribeToFormValueChanges();

    this.updatePaginationParamsFromQueryParams();
  }

  private getTenantData$(): Observable<ITenantsResponse> {
    return combineLatest([this.paginationParams$, this.reloadTenants$]).pipe(
      switchMap(([params, _]: [IOrganizationsPaginationParams, void]) => this.getTenants$(params)),
      AngularUtility.shareRef<ITenantsResponse>(),
    );
  }

  private getTotalRows$(): Observable<number> {
    return this.tenantsData$.pipe(map((data: ITenantsResponse): number => data[$tenants._total]));
  }

  private combineTenantPackageName(tenantsData: ITenantsResponse): ITenantWithPackageData[] {
    const packagesByTenantId: Record<string, IPackageInstanceIdName> = keyBy(
      tenantsData[$tenants.packages],
      $packageInstance.tenantId,
    );

    const addPackageToTenant = (tenant: ITenant): ITenantWithPackageData => ({
      ...tenant,
      [$tenantMetadata.packageData]: packagesByTenantId[tenant[$tenant._id]],
    });

    return tenantsData[$tenants.tenants].map(addPackageToTenant);
  }

  private getTableRows$(): Observable<ITableRow[]> {
    return combineLatest([this.tenantsData$, this.isSelfManagementEnabled$, this.tenantDecommissionFF$]).pipe(
      map(
        ([tenantsData, isSelfManagementEnabled, isTenantDecommissionEnabled]: [
          ITenantsResponse,
          boolean,
          boolean,
        ]): ITableRow[] =>
          this.combineTenantPackageName(tenantsData).map((tenant: ITenant): ITableRow =>
            this.buildTableRow(tenant, isSelfManagementEnabled, isTenantDecommissionEnabled),
          ),
      ),
    );
  }

  private buildTableRow(tenant: ITenant, isSelfManagementEnabled: boolean, isTenantDecommissionEnabled: boolean): ITableRow {
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;

    return {
      originalData: tenant,
      data: {
        [TableColumn.NAME]: isSuspended
          ? NgbTableUtilities.textCell({ text: tenant[$tenant.name] })
          : NgbTableUtilities.externalLinkCell({
              text: tenant[$tenant.name],
              showExternalLinkIcon: true,
              attributes: {
                href: this.buildTenantUrl(tenant),
              },
            }),
        [TableColumn.PACKAGE]: NgbTableUtilities.textCell({ text: this.getPackageColumnValue(tenant) }),
        [TableColumn.ADDONS]: NgbTableUtilities.htmlCell({ text: this.getPackageAddonsColumnValue(tenant) }),
        [TableColumn.SSO_CONFIG]: this.getSsoConfigCell(tenant),
        [TableColumn.ACADEMY_CONFIG]: this.getAcademyConfigCell(tenant),
        actions: NgbTableUtilities.dropdownCell({
          ellipsisIcon: true,
          classes: ['btn-link', 'px-0'],
          dropdownItems: this.buildDropdownItems(tenant, isSelfManagementEnabled, isTenantDecommissionEnabled),
        }),
      },
    };
  }

  private shouldShowManagementDetailsButton(isSelfManagementEnabled: boolean): boolean {
    if (!this.canReadWorkspace) {
      return false;
    }

    if (isSelfManagementEnabled) {
      return this.canReadDigitalAssets;
    }

    return true;
  }

  private buildDropdownItems(
    tenant: ITenant,
    isSelfManagementEnabled: boolean,
    isTenantDecommissionEnabled: boolean,
  ): IDropdownItem[] {
    const dropdownItems: IDropdownItem[] = [];
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;

    if (this.shouldShowManagementDetailsButton(isSelfManagementEnabled)) {
      dropdownItems.push({
        label: this.translateService.instant('management_details'),
        clickListener: (): void => this.viewTenant(tenant),
      });
    }

    if (this.canUpdateTenant) {
      dropdownItems.push({
        label: this.translateService.instant(`action_edit`),
        clickListener: (): void => this.editTenant(tenant),
      });
    }

    if (this.canUpdateTenant && SharedCommonUtility.notNullish(tenant[$tenant.ssoConfig])) {
      dropdownItems.push({
        label: this.translateService.instant(`sso_configuration`),
        clickListener: (): void => this.goToConfigureSso(tenant[$tenant._id]),
      });
    }

    if (this.canUpdateTenant) {
      if (isSuspended) {
        dropdownItems.push({
          label: this.translateService.instant('resume_tenant'),
          clickListener: (): void => this.confirmAndResumeTenant(tenant),
        });
      } else if (tenant[$tenant.status] === TenantStatus.active) {
        dropdownItems.push({
          label: this.translateService.instant('suspend_tenant'),
          clickListener: (): void => this.confirmAndSuspendTenant(tenant),
        });
      }
    }

    if (this.canDeleteTenant && isTenantDecommissionEnabled) {
      dropdownItems.push({
        label: this.translateService.instant('decommission_tenant'),
        clickListener: (): void => this.confirmAndDecommissionTenant(tenant),
      });
    }

    return dropdownItems;
  }

  private viewTenant(tenant: ITenant): void {
    this.router
      .navigate([tenant[$tenant._id]], { relativeTo: this.activatedRoute })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private editTenant(tenant: ITenant): void {
    this.router
      .navigate([tenant[$tenant._id], Api.edit], { relativeTo: this.activatedRoute })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private goToConfigureSso(tenantId: string): void {
    this.router
      .navigate([tenantId, Api.ssoConfig], { relativeTo: this.activatedRoute })
      .catch(this.errorHandlerService.handleRoutingError.bind(this.errorHandlerService));
  }

  private confirmAndDecommissionTenant(tenant: ITenant): void {
    this.confirmAndRunTenantAction(tenant, 'decommission', () => this.tenantService.decommissionTenant(tenant[$tenant._id]));
  }

  private confirmAndSuspendTenant(tenant: ITenant): void {
    this.confirmAndRunTenantAction(tenant, 'suspend', () => this.tenantService.suspendTenant(tenant[$tenant._id]));
  }

  private confirmAndResumeTenant(tenant: ITenant): void {
    this.confirmAndRunTenantAction(tenant, 'resume', () => this.tenantService.resumeTenant(tenant[$tenant._id]));
  }

  private confirmAndRunTenantAction(
    tenant: ITenant,
    action: 'decommission' | 'suspend' | 'resume',
    serviceCall: () => Observable<ITenant | void>,
  ): void {
    const modalRef = this.dsModalService.open(PromptConfirmationModalComponent, DsModalVariant.center);
    const modal: PromptConfirmationModalComponent = modalRef.componentInstance as PromptConfirmationModalComponent;
    const tenantName: string = tenant[$tenant.name];
    const modalOptions: PromptConfirmationModalOptions = {
      headerKey: `${action}_tenant_modal_title`,
      descriptionKey: `${action}_tenant${action === 'decommission' ? '_prompt' : '_modal'}_description`,
      descriptionParams: [tenantName],
      listHeaderKey: null,
      listItems: [],
      promptConfirmKey: `${action}_tenant_type_to_confirm`,
      promptConfirmParams: [tenantName],
      promptInputKey: `${action}_tenant_prompt_input`,
      promptInputParams: [tenantName],
      confirmButtonKey: `${action}_tenant_confirm`,
    };
    modal.init(modalOptions);

    this.subscription.add(
      modal.confirm.pipe(take(1)).subscribe((): void => {
        this.subscription.add(
          serviceCall().subscribe({
            next: (): void => {
              this.notificationService.success(
                this.translateService.instant(`${action}_tenant_success`),
                NotificationPosition.Toast,
              );
              this.reloadTenants$.next();
            },
            error: (error: HttpErrorResponse): void => {
              this.loadErrorHandler.handleError(`${action}_tenant_error`, error, this.constructor.name);
            },
          }),
        );
      }),
    );
  }

  private getSsoConfigCell(tenant: ITenant): IDclConfig<ILinkCellConfig | ITextCellConfig> {
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;

    if (isSuspended) {
      return NgbTableUtilities.textCell({
        text: this.translateService.instant('tenant_suspended'),
      });
    }

    if (SharedCommonUtility.notNullish(tenant[$tenant.ssoConfig])) {
      return NgbTableUtilities.textCell({
        text: this.translateService.instant('configured'),
        icon: {
          iconName: 'check-outline',
          iconHeight: '1rem',
          iconWidth: '1rem',
        },
      });
    }

    if (this.canCreateTenant) {
      return NgbTableUtilities.linkCell({
        text: this.translateService.instant('set_up_sso'),
        attributes: { routerLink: `./${tenant[$tenant._id]}/${Api.ssoConfig}` },
      });
    }

    return NgbTableUtilities.emptyCell();
  }

  private getAcademyConfigCell(tenant: ITenant): IDclConfig<ILinkCellConfig | ITextCellConfig> {
    const needsSetup: boolean = SharedCommonUtility.isNullish(
      tenant[$tenant.academyConfig]?.[$academy.externalPackageSignupField],
    );
    const isSuspended: boolean = tenant[$tenant.status] === TenantStatus.suspended;

    if (isSuspended) {
      return NgbTableUtilities.textCell({
        text: this.translateService.instant('tenant_suspended'),
      });
    }

    if (needsSetup) {
      return NgbTableUtilities.buttonCell({
        text: this.translateService.instant('set_up_academy'),
        classes: ['btn-link', 'text-underline', 'setup_academy_btn', 'p-0'],
        styles: { fontSize: 'inherit' },
        listeners: {
          click: this.setAcademy.bind(this, tenant),
        },
      });
    }

    return NgbTableUtilities.textCell({
      text: this.translateService.instant('configured'),
      icon: {
        iconName: 'check-outline',
        iconHeight: '1rem',
        iconWidth: '1rem',
      },
    });
  }

  public setAcademy(tenant: ITenant): void {
    this.editTenant(tenant);
  }

  private subscribeToPaginationParams(): void {
    this.subscription.add(
      this.paginationParams$.subscribe((params: IOrganizationsPaginationParams) => {
        this.pageNumber = params[$paginationParams.pageNumber];
        this.sortingOrder = SharedCommonUtility.notNullish(params[$paginationParams.sortBy])
          ? { [params[$paginationParams.sortBy]]: params[$paginationParams.direction] }
          : {};

        this.paginationService.updateUrlQueryParams(params);
        this.paginationService.updateFormValues(params, this.form, this.formFields);
        this.tableConfig = this.buildTableConfig();
      }),
    );
  }

  private subscribeToFormValueChanges(): void {
    this.subscription.add(
      this.paginationService.getFormValueChanges$(this.form, this.formFields).subscribe((params: Params) => {
        this.updatePaginationParams({
          ...params,
          [$paginationParams.pageNumber]: 1,
        });
      }),
    );
  }

  private updatePaginationParamsFromQueryParams(): void {
    const params: IOrganizationsPaginationParams = this.paginationService.getPaginationParamsFromQueryParams(
      this.defaultPaginationParams,
      this.sortableColumns,
      this.formFields,
    );

    this.updatePaginationParams(params);
  }

  private updatePaginationParams(update: Partial<IOrganizationsPaginationParams> = {}): void {
    this.paginationParams$.next({
      ...this.paginationParams$.value,
      ...update,
    });
  }

  public onPageNumberChanges(pageNumber: number): void {
    this.updatePaginationParams({
      [$paginationParams.pageNumber]: pageNumber,
    });
  }

  public onSort(event: SortEvent): void {
    this.updatePaginationParams({
      [$paginationParams.pageNumber]: 1,
      [$paginationParams.sortBy]: event.column,
      [$paginationParams.direction]: event.direction,
    });
  }

  public ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public buildTableConfig(): ITableConfig {
    const columns: Record<string, ITableColumn> = {
      [TableColumn.NAME]: {
        translationKey: 'tenant_name',
        sortingEnabled: true,
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '25%',
        },
      },
      [TableColumn.PACKAGE]: {
        translationKey: 'label_package',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '30%',
        },
      },
      [TableColumn.ADDONS]: {
        translationKey: 'label_addons',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '15%',
        },
      },
      [TableColumn.SSO_CONFIG]: {
        translationKey: 'sso',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '15%',
        },
      },
      [TableColumn.ACADEMY_CONFIG]: {
        translationKey: 'academy',
        styles: {
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '15%',
        },
      },
      actions: {
        translationKey: 'table_column_actions',
        styles: {
          width: '15%',
        },
      },
    };

    return {
      columns,
      caption: this.translateService.instant('table_caption_tenant_list'),
      emptyState: this.getTableEmptyState(),
    };
  }

  private buildTenantUrl(tenant: ITenant): string {
    return `https://${tenant[$tenant.domain]}.${this.tenantBaseUrl}`;
  }

  private getPackageColumnValue(tenant: ITenantWithPackageData): string {
    const packageData: IPackageInstanceIdName = tenant[$tenantMetadata.packageData];
    return SharedCommonUtility.notNullish(packageData)
      ? packageData[$packageInstance.packageDetails]?.[$package.name]
      : this.translateService.instant('no_package');
  }

  private getPackageAddonsColumnValue(tenant: ITenantWithPackageData): string {
    const packageAddonNames: IPackageAddonName[] = tenant[$tenantMetadata.packageData]?.[$packageInstance.packageAddons];
    return SharedCommonUtility.notNullishOrEmpty(packageAddonNames)
      ? packageAddonNames
          .map((packageAddonName: IPackageAddonName): string => packageAddonName[$packageAddon.name])
          .join('<br />')
      : this.translateService.instant('not_available_acronym');
  }
}
